import { useEffect, useRef, useState } from "react";

const INTERVAL_DURATION = 5;

// Calls 'onTriggered' when each key defined in 'keys' has been held down for
// 'pressDuration' milliseconds.
// Useful for putting together keypress combinations to trigger debug
// functionality that we don't want users to accidentally find.
export default function useDebugKeypress(
  keys: Array<string>,
  pressDuration: number = 60,
  onTriggered: () => void,
) {
  const timePressedRef = useRef(0);
  const intervalIdRef = useRef(0);
  const pressedKeysRef = useRef(new Set())
  const [triggered, setTriggered] = useState(false);

  useEffect(() => {
    function checkPressedKeys() {
      const allPressed = keys.every((key) => pressedKeysRef.current.has(key));
      if (allPressed) {
        timePressedRef.current += INTERVAL_DURATION;
        if (timePressedRef.current >= pressDuration && !triggered) {
          setTriggered(true);
          onTriggered();
        }
      } else {
        timePressedRef.current = 0;
        setTriggered(false);
      }
    }

    function onKeyDown(e: KeyboardEvent) {
      pressedKeysRef.current.add(e.key.toLowerCase());;
      if (intervalIdRef.current === 0) {
        intervalIdRef.current = setInterval(
          checkPressedKeys,
          INTERVAL_DURATION,
        );
      }
    }

    function onKeyUp(e: KeyboardEvent) {
      pressedKeysRef.current.delete(e.key.toLowerCase());
      if (pressedKeysRef.current.size === 0 && intervalIdRef.current !== null) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = 0;
        setTriggered(false);
      }
    }

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      if (intervalIdRef.current !== 0) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = 0;
      }
    };
  }, [keys, pressDuration, onTriggered, triggered]);
}
